import React, { useReducer, useState, useMemo, useEffect } from "react";
import { 
    Container, Grid, TextField, Typography, MenuItem, 
    InputAdornment, Paper, Alert, Skeleton 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useHistory, useParams } from "react-router-dom";

// Componentes
import CustomBreadcrumbs from "../../../../components/breadcrumbs";
import ResponseComponent from "../../../../components/responseComponent";

// Controlador
import { getPlan, updatePlan } from "../../../../controllers/company/subscriptions/PlansController";

const initialState = {
    name: '',           errorName: false,
    description: '',
    price: '',          errorPrice: false,
    interval_count: 1,  errorIntervalCount: false,
    billing_interval: 'month',
    trial_days: 0,
    status: 1,          // Añadimos estatus para poder desactivarlo

    isLoading: false,   // Para el guardado
    isFetching: true,   // Para la carga inicial
    open: false,
    title: '',
    message: '',
    success: false
};

function reducer(state, { field, value }) {
    return { ...state, [field]: value };
}

export default function PlanEdit() {
    const [state, setState] = useReducer(reducer, initialState);
    const history = useHistory();
    const { id } = useParams(); // Obtenemos el ID de la URL

    const {
        name, errorName, description,
        price, errorPrice,
        interval_count, errorIntervalCount, billing_interval,
        trial_days, status,
        isLoading, isFetching, open, title, message, success
    } = state;

    // --- CARGAR DATOS ---
    useEffect(() => {
        const loadData = async () => {
            const response = await getPlan(id);
            if (response.success) {
                const plan = response.data;
                // Rellenamos el estado con los datos de la API
                setState({ field: 'name', value: plan.name });
                setState({ field: 'description', value: plan.description || '' });
                setState({ field: 'price', value: plan.price });
                setState({ field: 'interval_count', value: plan.interval_count });
                setState({ field: 'billing_interval', value: plan.billing_interval });
                setState({ field: 'trial_days', value: plan.trial_days });
                setState({ field: 'status', value: plan.status ? 1 : 0 });
            } else {
                // Manejar error de carga (ej. redirigir o mostrar alerta)
                alert("Error al cargar el plan");
                history.push('/subscriptions/plans');
            }
            setState({ field: 'isFetching', value: false });
        };
        loadData();
    }, [id, history]);

    const onChange = (e) => {
        setState({ field: e.target.name, value: e.target.value });
    };

    const summaryText = useMemo(() => {
        if (!price) return "Define el precio y frecuencia.";
        const currency = "$";
        const map = { day: 'días', week: 'semanas', month: 'meses', year: 'años' };
        const singularMap = { day: 'día', week: 'semana', month: 'mes', year: 'año' };
        let frequency = parseInt(interval_count) === 1 
            ? `cada ${singularMap[billing_interval]}` 
            : `cada ${interval_count} ${map[billing_interval]}`;
        return `El cliente pagará ${currency}${parseFloat(price || 0).toFixed(2)} ${frequency}.`;
    }, [price, interval_count, billing_interval]);

    const formVerification = () => {
        let error = false;
        if (name === '') { setState({ field: 'errorName', value: true }); error = true; }
        if (price === '' || parseFloat(price) < 0) { setState({ field: 'errorPrice', value: true }); error = true; }
        if (interval_count === '' || parseInt(interval_count) < 1) { setState({ field: 'errorIntervalCount', value: true }); error = true; }
        return !error;
    };

    const handleUpdate = async () => {
        if (!formVerification()) return;
        setState({ field: 'isLoading', value: true });

        const data = {
            name,
            description,
            price,
            interval_count,
            billing_interval,
            trial_days,
            status: status == 1 // Convertir a booleano
        };

        const response = await updatePlan(id, data);

        if (response.success) {
            setState({ field: 'title', value: 'Plan Actualizado' });
            setState({ field: 'message', value: 'Los cambios se han guardado correctamente.' });
            setState({ field: 'success', value: true });
            setState({ field: 'open', value: true });
        } else {
            setState({ field: 'title', value: 'Error' });
            setState({ field: 'message', value: response.message });
            setState({ field: 'success', value: false });
            setState({ field: 'open', value: true });
        }
        setState({ field: 'isLoading', value: false });
    };

    const handleCloseResponse = () => {
        if (success) history.push('/subscriptions');
        else setState({ field: 'open', value: false });
    };

    if (isFetching) {
        return (
            <Container maxWidth="md" sx={{ mt: 5 }}>
                <Skeleton variant="text" height={60} width="40%" />
                <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <CustomBreadcrumbs 
                returnTo='/subscriptions' 
                crumbs={[
                    { name: 'Suscripciones', link: '/subscriptions' },
                    { name: 'Planes', link: '/subscriptions/plans' },
                    { name: 'Editar', link: '#' }
                ]}
            />

            <Typography variant="h4" sx={{ mt: 4, mb: 4 }}>
                Editar Plan
            </Typography>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Grid container spacing={3}>
                    
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>Información General</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Nombre del Plan"
                            name="name"
                            value={name}
                            onChange={onChange}
                            error={errorName}
                            helperText={errorName && "El nombre es requerido"}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Precio"
                            name="price"
                            type="number"
                            value={price}
                            onChange={onChange}
                            error={errorPrice}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Grid>

                    {/* Campo de Estatus para poder desactivar el plan */}
                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            label="Estatus"
                            name="status"
                            value={status}
                            onChange={onChange}
                        >
                            <MenuItem value={1}>Activo</MenuItem>
                            <MenuItem value={0}>Inactivo</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Descripción (Opcional)"
                            name="description"
                            multiline
                            rows={2}
                            value={description}
                            onChange={onChange}
                        />
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Frecuencia de Cobro</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography>Cobrar cada:</Typography>
                            <TextField
                                sx={{ width: '80px' }}
                                name="interval_count"
                                type="number"
                                value={interval_count}
                                onChange={onChange}
                                error={errorIntervalCount}
                            />
                            <TextField
                                select
                                sx={{ flexGrow: 1 }}
                                name="billing_interval"
                                value={billing_interval}
                                onChange={onChange}
                            >
                                <MenuItem value="week">Semanas</MenuItem>
                                <MenuItem value="month">Meses</MenuItem>
                                <MenuItem value="year">Años</MenuItem>
                            </TextField>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Días de Prueba (Gratis)"
                            name="trial_days"
                            type="number"
                            value={trial_days}
                            onChange={onChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">Resumen:</Typography>
                            {summaryText}
                        </Alert>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <LoadingButton
                            variant="contained"
                            size="large"
                            fullWidth
                            loading={isLoading}
                            onClick={handleUpdate}
                        >
                            Guardar Cambios
                        </LoadingButton>
                    </Grid>

                </Grid>
            </Paper>

            <ResponseComponent
                show={open}
                success={success}
                title={title}
                message={message}
                actionText="Aceptar"
                onClick={handleCloseResponse}
            />
        </Container>
    );
}